import { useCallback } from 'react';

import { CombinedGraphQLErrors } from '@apollo/client/errors';

import {
  ANALYTICS_PROVIDER,
  type AnalyticsEvent,
  type EventProperties,
  useAnalytics,
} from '@shared/analytics';

/**
 * MLS analytics helper. Mirrors `getBaseCmaEventProperties` but wraps
 * `useAnalytics().track` so MLS call sites don't repeat the base property bag
 * or the provider-routing decision.
 *
 * Routing (confirmed product decision):
 * - Pure UI/browse intent (`trackMls`) → PostHog only.
 * - Mutation-backed actions (`trackMlsMutation`) emit a distinct event on
 *   success vs failure. A *successful* lifecycle action goes to PostHog +
 *   Customer.io (to drive agent messaging); a *failed* action fires its own
 *   `*_failed` event (with `error_reason`) to PostHog only, and every
 *   non-lifecycle success is also PostHog-only — so Customer.io never sees an
 *   outcome that didn't happen.
 */
type MlsEventUser = {
  id?: string | null;
  tenant_id?: string | null;
  tenant?: {
    name?: string | null;
  } | null;
};

export const getBaseMlsEventProperties = (
  me: MlsEventUser | null | undefined,
) => {
  if (!me?.id || !me?.tenant_id) {
    return null;
  }

  return {
    user_id: me.id,
    tenant_id: me.tenant_id,
    tenant_name: me.tenant?.name,
  };
};

// Browse / UI / map intent → PostHog only (excluded from Customer.io).
const POSTHOG_ONLY = { exclude: [ANALYTICS_PROVIDER.CUSTOMERIO] };

export type MlsMutationOutcome = {
  success: boolean;
  // Safe, non-PII Hasura error code (e.g. 'permission-error') when known.
  errorReason?: string;
};

const extractErrorCode = (extensions: unknown): string | undefined => {
  if (
    extensions != null &&
    typeof extensions === 'object' &&
    'code' in extensions
  ) {
    const { code } = extensions;
    return typeof code === 'string' ? code : undefined;
  }
  return undefined;
};

/**
 * A mutation's root field is "meaningful" (the write actually happened) when it
 * returns a row object, a non-empty `returning`/list, or `affected_rows > 0`.
 * Hasura `update/delete_..._by_pk` return `null` (no GraphQL error) when the row
 * is filtered out by a row-level permission or absent — that must count as a
 * failure, which a bare `data != null` check would miss.
 */
const isMeaningfulResult = (value: unknown): boolean => {
  if (value == null) {
    return false;
  }
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  if (typeof value === 'object') {
    if ('affected_rows' in value) {
      const { affected_rows: rows } = value;
      return typeof rows === 'number' ? rows > 0 : true;
    }
    return true; // a returned row object
  }
  return true; // scalar (rare for a mutation payload)
};

/**
 * Build an outcome from an awaited Apollo mutation result. Success requires the
 * returned payload to actually reflect a write (see `isMeaningfulResult`), not
 * merely a non-null `data` wrapper. Extracts the first GraphQL error's Hasura
 * code, if any.
 */
export const toMlsOutcome = (result: {
  data?: unknown;
  errors?: ReadonlyArray<{ extensions?: unknown }> | null;
}): MlsMutationOutcome => {
  const data = result?.data;
  const success =
    data != null &&
    typeof data === 'object' &&
    Object.values(data).some(isMeaningfulResult);
  return {
    success,
    errorReason: success
      ? undefined
      : extractErrorCode(result?.errors?.[0]?.extensions),
  };
};

/** Build a failure outcome from a caught error (mutations that reject). */
export const toMlsErrorOutcome = (error: unknown): MlsMutationOutcome => {
  // Apollo v4 wraps GraphQL errors in CombinedGraphQLErrors (`.errors`), not the
  // v3 `.graphQLErrors` field — same accessor the CRM's MutationErrorModal uses.
  const graphQLErrors = CombinedGraphQLErrors.is(error) ? error.errors : [];
  return {
    success: false,
    errorReason: extractErrorCode(graphQLErrors[0]?.extensions),
  };
};

/**
 * Run an Apollo mutation and normalise its result into an outcome, whether the
 * mutation resolves-on-error (has an `onError`) or rejects. The caller still
 * gets the mutation's own error side effects (snackbars) via its `onError`.
 */
export const runMlsMutation = async (
  mutate: () => Promise<{
    data?: unknown;
    errors?: ReadonlyArray<{ extensions?: unknown }> | null;
  }>,
): Promise<MlsMutationOutcome> => {
  try {
    return toMlsOutcome(await mutate());
  } catch (error) {
    return toMlsErrorOutcome(error);
  }
};

export const useMlsTracking = (me: MlsEventUser | null | undefined) => {
  const { track } = useAnalytics();

  // Pure UI / browse intent — always PostHog-only, no outcome.
  const trackMls = useCallback(
    (event: AnalyticsEvent, properties?: EventProperties) => {
      const base = getBaseMlsEventProperties(me);
      if (base == null) {
        return;
      }
      track(event, { ...base, ...properties }, POSTHOG_ONLY);
    },
    [track, me],
  );

  // Mutation-backed action with a known outcome. Success fires `successEvent`
  // (→ Customer.io too when `lifecycle`); failure fires the distinct
  // `failedEvent` (with error_reason) to PostHog only. Pass `failedEvent: null`
  // for actions with no observable failure path (e.g. bulk email send).
  const trackMlsMutation = useCallback(
    (
      successEvent: AnalyticsEvent,
      failedEvent: AnalyticsEvent | null,
      properties: EventProperties | undefined,
      outcome: MlsMutationOutcome,
      options?: { lifecycle?: boolean },
    ) => {
      const base = getBaseMlsEventProperties(me);
      if (base == null) {
        return;
      }
      if (outcome.success) {
        const merged: EventProperties = { ...base, ...properties };
        if (options?.lifecycle === true) {
          track(successEvent, merged);
        } else {
          track(successEvent, merged, POSTHOG_ONLY);
        }
        return;
      }
      if (failedEvent == null) {
        return;
      }
      // Failures are always PostHog-only.
      track(
        failedEvent,
        {
          ...base,
          ...properties,
          ...(outcome.errorReason != null
            ? { error_reason: outcome.errorReason }
            : {}),
        },
        POSTHOG_ONLY,
      );
    },
    [track, me],
  );

  // Run a mutation and track its outcome in one step. Use for awaited call
  // sites; the returned outcome lets the caller gate its own success/failure
  // side effects. Tracking runs off the awaited promise (not a lifecycle-bound
  // `onCompleted`), so it still fires if the component unmounts mid-flight.
  const trackMlsAttempt = useCallback(
    (
      successEvent: AnalyticsEvent,
      failedEvent: AnalyticsEvent | null,
      properties: EventProperties | undefined,
      mutate: () => Promise<{
        data?: unknown;
        errors?: ReadonlyArray<{ extensions?: unknown }> | null;
      }>,
      options?: { lifecycle?: boolean },
    ): Promise<MlsMutationOutcome> =>
      runMlsMutation(mutate).then(outcome => {
        trackMlsMutation(
          successEvent,
          failedEvent,
          properties,
          outcome,
          options,
        );
        return outcome;
      }),
    [trackMlsMutation],
  );

  return { trackMls, trackMlsMutation, trackMlsAttempt };
};
